import { JSX } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { ValueType } from 'components/DebounceSelect';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { Typography } from 'components/typography';
import { CompanyCoreInfo } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { GenericCompanySearchableFields } from 'utils/loadInfoAndBuilding';

import {
  PLS_BILL_TO_SEARCH_API_ENABLED,
  PLS_SECTION_LABELS,
} from './constants';
import { PlsFormValues } from './types';
import { mapPlsBillTosToSelectOptions } from './utils';

export type BillToSectionFormProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  billTos: Maybe<CompanyCoreInfo[]>;
  isLoadingBillTos: boolean;
  handleBillToSearch: (
    field: GenericCompanySearchableFields,
    value: string
  ) => Promise<ValueType[]>;
  refreshBillTos: () => void;
  isBillToSearchApiEnabled?: boolean;
  isCreateMode?: boolean;
  aiHighlightedExternalTMSID?: string;
};

export function BillToSectionForm({
  formMethods,
  billTos,
  isLoadingBillTos,
  handleBillToSearch,
  refreshBillTos,
  isBillToSearchApiEnabled = PLS_BILL_TO_SEARCH_API_ENABLED,
  isCreateMode = true,
  aiHighlightedExternalTMSID,
}: BillToSectionFormProps): JSX.Element {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = formMethods;

  const watchedCustomerId = watch('customer.externalTMSID');

  const handleBillToSelection = (
    _value: ValueType,
    selectedRawItem?: unknown
  ) => {
    if (!selectedRawItem) {
      return;
    }

    Object.entries(selectedRawItem as CompanyCoreInfo).forEach(
      ([key, value]) => {
        setValue(`billTo.${key}` as FieldPath<PlsFormValues>, value, {
          shouldDirty: true,
        });
      }
    );
    setValue('plsOperationalFields', {}, { shouldDirty: true });
  };

  if (!watchedCustomerId) {
    return (
      <Typography variant='body-sm' className='text-neutral-600'>
        Select a customer to choose a bill-to.
      </Typography>
    );
  }

  if (!isBillToSearchApiEnabled) {
    return (
      <Typography variant='body-sm' className='text-neutral-600'>
        Bill-to search is not available yet.
      </Typography>
    );
  }

  return (
    <>
      <RHFDebounceSelect
        required
        name='billTo.externalTMSID'
        label={PLS_SECTION_LABELS.billTo}
        control={control}
        errors={errors}
        data={billTos}
        fetchOptions={handleBillToSearch}
        mapOptions={mapPlsBillTosToSelectOptions}
        refreshHandler={refreshBillTos}
        isLoading={isLoadingBillTos}
        placeholder='Search bill-to'
        showSearchParamDropdown={false}
        parentOnChange={handleBillToSelection}
        callFieldOnChangeWithParent
        disabled={!isCreateMode}
        aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
      />
    </>
  );
}
